<template>
  <div class="drawer lg:drawer-open">
    <input
      id="main-drawer"
      v-model="drawerOpen"
      type="checkbox"
      class="drawer-toggle"
    >
    <div class="drawer-content flex flex-col">
      <header class="navbar gap-2 lg:hidden">
        <label
          for="main-drawer"
          class="btn btn-square btn-ghost"
          aria-label="Menü öffnen"
        >
          <font-awesome-icon
            :icon="faBars"
            class="text-xl"
          />
        </label>
        <span class="font-bold">Karten-Designer</span>
      </header>
      <main class="px-8 py-6 m-4 rounded-lg shadow-xl">
        <slot />
      </main>
    </div>

    <nav class="drawer-side z-20">
      <label
        for="main-drawer"
        class="drawer-overlay"
        aria-label="Menü schließen"
      />
      <div class="w-80 bg-base-100 text-base-content min-h-full">
        <div class="flex items-center justify-between pl-4 pr-2 py-2">
          <h1 class="font-bold">
            Karten-Designer
          </h1>
          <div class="flex">
            <div class="dropdown dropdown-end">
              <div
                id="theme-menu"
                tabindex="0"
                role="button"
                class="btn btn-ghost btn-circle"
                title="Darstellung"
              >
                <font-awesome-icon
                  :icon="themeOptions[choice].icon"
                  class="text-xl"
                />
              </div>
              <ul
                id="theme-options"
                tabindex="-1"
                class="dropdown-content menu bg-base-100 rounded-box z-10 w-40 p-2 shadow-sm"
              >
                <li
                  v-for="(option, value) in themeOptions"
                  :key="value"
                >
                  <button
                    type="button"
                    :class="{ 'menu-active': choice === value }"
                    @click="chooseTheme(value)"
                  >
                    <font-awesome-icon :icon="option.icon" />
                    {{ option.label }}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="dropdown w-full px-2 pb-2">
          <div
            id="account-menu"
            tabindex="0"
            role="button"
            class="btn btn-ghost h-auto w-full justify-start gap-3 py-2 text-left font-normal"
            title="Konto"
          >
            <font-awesome-icon
              :icon="faCircleUser"
              class="text-3xl text-primary"
            />
            <span class="flex min-w-0 flex-col">
              <span class="truncate font-semibold">{{ account.name }}</span>
              <span
                v-if="account.email && account.email !== account.name"
                class="truncate text-xs text-base-content/60"
              >{{ account.email }}</span>
            </span>
          </div>
          <ul
            tabindex="-1"
            class="dropdown-content menu bg-base-100 rounded-box z-10 w-52 p-2 shadow-sm"
          >
            <li>
              <a
                :href="accountHref"
                target="_blank"
                rel="noopener"
              >
                <font-awesome-icon :icon="faUser" />
                Profil
              </a>
            </li>
            <li>
              <button
                type="button"
                @click="signOut"
              >
                <font-awesome-icon :icon="faRightFromBracket" />
                Abmelden
              </button>
            </li>
          </ul>
        </div>
        <ul class="menu bg-base-200 w-full px-4">
          <li class="menu-title">
            Bereiche
          </li>
          <li>
            <NuxtLink
              to="/design/"
              active-class="active"
            >
              <font-awesome-icon :icon="faImages" />
              Entwerfen
            </NuxtLink>
          </li>
          <li>
            <NuxtLink
              to="/write/"
              active-class="active"
            >
              <font-awesome-icon :icon="faKeyboard" />
              Schreiben
            </NuxtLink>
          </li>
          <li>
            <NuxtLink
              to="/print/"
              active-class="active"
            >
              <font-awesome-icon :icon="faPrint" />
              Drucken
            </NuxtLink>
          </li>
        </ul>
        <ul class="menu w-full">
          <slot name="menu" />
        </ul>
      </div>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { faBars, faCircleHalfStroke, faCircleUser, faImages, faKeyboard, faMoon, faPrint, faRightFromBracket, faSun, faUser } from '@fortawesome/free-solid-svg-icons'
import { type ThemeChoice, useTheme } from '~/composables/theme'

const accountHref = ref<string>()
accountUrl().then((url) => {
  accountHref.value = url
})

const account = ref<{ name: string, email?: string }>({ name: '' })
signedInAs().then((signedIn) => {
  account.value = signedIn
})

// On small screens the menu is a drawer; close it once a link was followed
const drawerOpen = ref(false)
const route = useRoute()
watch(() => route.fullPath, () => {
  drawerOpen.value = false
})

const { choice, setTheme } = useTheme()
const themeOptions = {
  system: { label: 'System', icon: faCircleHalfStroke },
  light: { label: 'Hell', icon: faSun },
  dark: { label: 'Dunkel', icon: faMoon },
}

function chooseTheme(value: ThemeChoice) {
  setTheme(value)
  // daisyUI dropdowns stay open while they have the focus
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }
}
</script>
